import * as R from 'ramda'
import React from 'react'

import { Icon } from '../../../src/ui/icons/Icon.jsx'
import { locationToEndpoint } from '../../../src/utils/location.js'

const visualCommands = [
  { command: 'clearHeatmap' },
  {
    command: 'clearLines',
    args: [
      { name: 'groupName', type: 'string' }
    ]
  },
  {
    command: 'displayHeatmap',
    args: [
      { name: 'data', type: 'list', itemType: { type: 'list', itemType: { type: 'float' } } },
      { name: 'weightMax', type: 'float', optional: true }
    ]
  },
  {
    command: 'drawLines',
    args: [
      { name: 'groupName', type: 'string' },
      { name: 'coords', type: 'list', itemType: { type: 'list', itemType: { type: 'float' } } },
      {
        name: 'style',
        type: 'object',
        optional: true,
        props: [
          { name: 'color', type: 'string', optional: true },
          { name: 'gradient', type: 'list', itemType: { type: 'string' }, optional: true },
          { name: 'width', type: 'integer', optional: true }
        ]
      },
      { name: 'ord', type: 'integer', optional: true }
    ]
  },
  {
    command: 'drawMarker',
    args: [
      { name: 'name', type: 'string' },
      { name: 'at', type: 'location', optional: true },
      { name: 'imageURL', type: 'string', optional: true },
      {
        name: 'options',
        type: 'object',
        optional: true,
        props: [
          { name: 'anchor', type: 'string', optional: true }, // 'center' , 'top' , 'bottom' , 'left' , 'right' , 'top-left' , 'top-right' , 'bottom-left' , and 'bottom-right'
          { name: 'color', type: 'string', optional: true },
          { name: 'hoverHTML', type: 'string', optional: true },
          { name: 'offset', type: 'list', optional: true, itemType: { type: 'integer' } }, // [x,y] offset
          { name: 'pitchAlignment', type: 'string', optional: true },
          { name: 'rotation', type: 'float', optional: true },
          { name: 'rotationAlignment', type: 'string', optional: true },
          { name: 'scale', type: 'float', optional: true }
        ]
      }
    ]
  },
  { command: 'getPosition' },
  { command: 'getState' },
  { command: 'getZoom' },
  { command: 'hideMarker', args: [{ name: 'name', type: 'string' }] },
  { command: 'hidePOIResults' },
  { command: 'resetMap' },
  { command: 'resetSearch' },
  { command: 'setBearing', args: [{ name: 'bearing', type: 'float' }] },
  { command: 'setLanguage', args: [{ name: 'languageCode', type: 'string' }] },
  { command: 'setPitch', args: [{ name: 'pitch', type: 'float' }] },
  { command: 'setPosition', args: [{ name: 'location', type: 'location' }] },
  { command: 'setState', args: [{ name: 'state', type: 'string' }] },
  {
    command: 'setViewportBounds',
    args: [
      { name: 'minLat', type: 'float' },
      { name: 'maxLat', type: 'float' },
      { name: 'minLng', type: 'float' },
      { name: 'maxLng', type: 'float' },
      { name: 'ord', type: 'integer', optional: true }
    ]
  },
  { command: 'showLevel', args: [{ name: 'ord', type: 'integer' }] },
  {
    command: 'showNavigation',
    args: [
      { name: 'from', type: 'location' },
      { name: 'to', type: 'location' },
      { name: 'accessible', type: 'boolean', optional: true },
      { name: 'queueTypes', type: 'list', itemType: { type: 'string' }, optional: true }
    ]
  },
  {
    command: 'showNavigationMultiple',
    args: [
      { name: 'locations', type: 'list', itemType: { type: 'location' } },
      { name: 'accessible', type: 'boolean', optional: true },
      { name: 'queueTypes', type: 'list', itemType: { type: 'string' }, optional: true }
    ]
  },
  { command: 'showPOI', args: [{ name: 'id', type: 'integer' }] },
  { command: 'showPOIResults', args: [{ name: 'poiIds', type: 'list', itemType: { type: 'integer' } }] },
  { command: 'showSearch', args: [{ name: 'term', type: 'string' }] },
  { command: 'showStructure', args: [{ name: 'structureId', type: 'string' }] },
  { command: 'switchVenue', args: [{ name: 'venueId', type: 'string' }] },
  { command: 'unmount' },
  { command: 'zoomBy', args: [{ name: 'value', type: 'float' }] },
  { command: 'zoomTo', args: [{ name: 'value', type: 'float' }] }
]

const getCurrentAt = async app =>
  app.bus.get('map/getMapCenter')
    .then(({ lat, lng, ordinal }) => ({ lat, lng, ord: ordinal }))

function handleVisual (app, sendEvent) {
  // clearHeatmap
  app.bus.on('clientAPI/clearHeatmap', async () => app.bus.get('map/clearHeatmap'))

  // clearLines
  app.bus.on('clientAPI/clearLines', async ({ groupName }) => app.bus.get('map/clearLines', { groupName }))

  // drawLines
  app.bus.on('clientAPI/drawLines', async ({ groupName, coords, ord, style }) => app.bus.get('map/drawLines', { groupName, coords, ord, style }))

  // drawMarker
  app.bus.on('clientAPI/drawMarker', async ({ name, at, imageURL, options = {} }) => {
    if (!at)
      at = await getCurrentAt(app)

    const ep = await locationToEndpoint(app, at)

    const markerOptions = R.pick(['anchor', 'offset', 'color', 'scale', 'rotation', 'pitchAlignment', 'rotationAlignment'], options) // These options map to markerOptions, hoverHTML is a drawMarker "option", but for the map/addMarker command its separate [Note: scale/color only works for default marker, while hoverHTML only works for custom image markers]

    let marker
    const onClick = () => sendEvent('markerClicked', { name, at, imageURL })
    if (imageURL && !imageURL.startsWith('icon:'))
      marker = <img style={{ display: 'block' }} src={imageURL} onClick={ onClick }/>
    else {
      if (imageURL) {
        // User will be using an icon - either explicitly specified via imageURL = "icon:<name>" or taking default
        const iconName = imageURL ? imageURL.substring(5) : 'wayfinding.end'
        marker = <Icon id={ iconName } width={34} height={47} onClick={ onClick }/>
      }
    }
    app.bus.send('map/addMarker', { id: name, ordinal: ep.ordinal, lat: ep.lat, lng: ep.lng, markerComponent: marker, hoverHTML: options.hoverHTML, markerOptions })
  })

  // getPosition
  app.bus.on('clientAPI/getPosition', async () => {
    const { lat, lng, ordinal, structureId, floorId } = await app.bus.get('map/getMapCenter')
    const { zoom, pitch, bearing } = (await app.bus.get('map/getCurrentView'))
    return { lat, lng, ord: ordinal, structureId, floorId, zoom, pitch, bearing }
  })

  // getState
  app.bus.on('clientAPI/getState', () => app.bus.get('deepLinking/getEncodedState'))

  // getZoom
  app.bus.on('clientAPI/getZoom', () => app.bus.get('map/getCurrentView').then(vpo => vpo.zoom))

  // hideMarker (name)
  app.bus.on('clientAPI/hideMarker', ({ name }) => app.bus.send('map/removeMarker', { id: name }))

  // hidePOIResults (hides all results)
  app.bus.on('clientAPI/hidePOIResults', () => app.bus.send('map/selectEntities', { ids: [] })
    .then(() => true))

  // resetMap
  app.bus.on('clientAPI/resetMap', () => app.bus.send('app/reset'))

  // resetSearch
  app.bus.on('clientAPI/resetSearch', () => app.bus.send('headerOnline/show'))

  // setBearing
  app.bus.on('clientAPI/setBearing', ({ bearing }) => app.bus.send('map/setBearing', { bearing }).then(() => true))

  // setLanguage
  app.bus.on('clientAPI/setLanguage', ({ languageCode }) => app.bus.send('i18n/setUILang', { lang: languageCode }).then(() => true))

  // setPitch
  app.bus.on('clientAPI/setPitch', ({ pitch }) => app.bus.send('map/setPitch', { pitch }).then(() => true))

  // setPosition
  app.bus.on('clientAPI/setPosition', async ({ location }) => {
    const ep = await locationToEndpoint(app, location)
    const { lat, lng, floorId } = ep
    const zoom = location.zoom // optional
    app.bus.send('map/animateToPoint', { lat, lng, floorId, zoom, animOptions: R.pick(['pitch', 'bearing'], location) })
    return true
  })

  // setState
  app.bus.on('clientAPI/setState', ({ state }) => app.bus.send('deepLinking/setEncodedState', { state }).then(() => true))

  // setViewportBounds
  // app.bus.on('map/centerInBounds', async ({ bounds, bearing = null, padding = null, floorId = null, pitch = null }) => {
  app.bus.on('clientAPI/setViewportBounds', ({ minLat, maxLat, minLng, maxLng, ord }) => {
    const bounds = {
      n: maxLat,
      s: minLat,
      w: minLng,
      e: maxLng
    }
    app.bus.send('map/centerInBounds', { bounds })
    if (ord !== undefined)
      app.bus.send('map/changeOrdinal', { ordinal: ord })

    return true
  })

  // displayHeatmap (data)
  app.bus.on('clientAPI/displayHeatmap', ({ data, weightMax }) => app.bus.send('map/displayHeatmap', { data, weightMax }).then(() => true))

  // showLevel (ord)
  app.bus.on('clientAPI/showLevel', ({ ord }) => app.bus.send('map/changeOrdinal', { ordinal: ord }).then(() => true))

  // showNavigation (from, to, accessible) (use "location" format)
  app.bus.on('clientAPI/showNavigation', async ({ from, to, accessible, queueTypes }) => {
    const fromEndpoint = await locationToEndpoint(app, from)
    const toEndpoint = await locationToEndpoint(app, to)
    const args = { endpointIds: [fromEndpoint, toEndpoint], requiresAccessibility: accessible, referrer: 'prog' }
    if (queueTypes)
      args.selectedSecurityLanes = { SecurityLane: queueTypes }
    return app.bus.get('navigation/showDirectionsFromTo', args)
  })

  app.bus.on('clientAPI/showNavigationMultiple', async ({ locations, accessible, queueTypes }) => {
    const endpointIds = await Promise.all(locations.map(l => locationToEndpoint(app, l)))
    const args = { endpointIds, requiresAccessibility: accessible, referrer: 'prog' }
    if (queueTypes)
      args.selectedSecurityLanes = { SecurityLane: queueTypes }
    return app.bus.get('navigation/showDirectionsFromTo', args)
  })

  // showPOI (id)
  app.bus.on('clientAPI/showPOI', ({ id }) => app.bus.send('poiDetails/showPoi', { poi: id }).then(() => true))

  // showPOIResults (accept only list of POIs)
  //   { command: 'showPOIResults', args: [{ name: 'poiIds', type: 'list', itemType: { type: 'integer' } }] },
  //     app.bus.send('map/selectEntities', { ids: Object.values(pois).map(poi => poi.poiId) })
  app.bus.on('clientAPI/showPOIResults', ({ poiIds }) =>
    app.bus.send('map/selectEntities', { ids: poiIds.map(id => '' + id) }) // they must be strings.. ¯\_(ツ)_/¯
      .then(() => true))

  // showSearch
  app.bus.on('clientAPI/showSearch', ({ term }) => app.bus.send('homeview/performConfirmedSearch', { term, referrer: 'prog' }).then(() => true))

  // showStructure
  app.bus.on('clientAPI/showStructure', ({ structureId }) => app.bus.send('mapLevelSelector/selectBuilding', { id: structureId })
    .then(() => true))

  // switchVenue
  app.bus.on('clientAPI/switchVenue', ({ venueId }) =>
    app.bus.send('venueData/loadNewVenue', { venueId, accountId: app.config.plugins.venueDataLoader.accountId }).then(() => true))

  // unmount
  app.bus.on('clientAPI/unmount', () => app.bus.send('app/unmount').then(() => true))

  // zoomBy
  app.bus.on('clientAPI/zoomBy', ({ value }) => app.bus.send('map/zoomBy', { value }).then(() => true))

  // zoomTo
  app.bus.on('clientAPI/zoomTo', ({ value }) => app.bus.send('map/zoomTo', { value }).then(() => true))
}

export {
  visualCommands,
  handleVisual
}
